// screen: Channel page — Monitor · Ingest · Output · Recording · Ads · Programmes · Catch-up · Clips · Settings (live spec)
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '~/app/api';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, Copy, Download, Play, Trash2, Wand2, AlertTriangle } from 'lucide-react';
import { ApiError } from '@viewstream/api-client';
import { Badge, Button, ConfirmByNameDialog, Dialog, DialogContent, EmptyState, ErrorState, Field, Input, Select, Skeleton, Switch, Tabs, TabsContent, TabsList, TabsTrigger, useToast } from '@viewstream/ui';
import { Player } from '@viewstream/player-react';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { ChannelBlackouts } from '~/app/Security';
import { LipSyncHealth, LipSyncSwitches } from '~/app/LipSync';
import { InputMonitorPanel } from '~/app/InputMonitor';
import { ChannelAvailability } from '~/app/ChannelAvailability';
import { ChannelPackaging } from '~/app/Packaging';
import { ModeToggle, useUiMode } from '~/app/uiMode';
import { ChannelStats } from '~/app/ContentStats';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { CatchUp, PlayerLiveMonitor } from '~/app/CatchUp';
import { EmbedShare } from '~/app/EmbedShare';
import { PresetPicker } from '~/app/PresetPicker';
import { useSections } from '~/app/presets';
import { AUTO_SECTION, useLiveToVODRule, usePutLiveToVODRule, type LiveToVODRule } from '~/app/programmeVod';
import { watchUrl } from '~/app/embed';
import { IngestQuickControls, IngestSettings } from '~/app/IngestSettings';
import { RecordingMode } from '~/app/RecordingMode';
import { BackupPathway } from '~/app/BackupPathway';
import { EncodersCard } from '~/app/EncodersCard';
import { ChannelAds } from '~/app/ChannelAds';
import { ExclusionBadge, ProgrammeExclusionAction } from '~/app/CatchupExclusions';
import { exclusionOf, isExcluded } from '~/app/catchupExclusions';
import { VideoId } from '~/app/VideoId';
import { ChannelPlacementSettings, InternalBadge, isInternal } from '~/app/InternalChannel';
import { CleanLinkCard, ClipFallbackBadge, ContributionHint } from '~/app/CleanLink';
import type { ContributionRec } from '@viewstream/api-client';
import { ChannelStateBadge, ClipStatusBadge, FeedBadges, feedsOf, hms, waitingForFirstFeed, channelDeletable, useChannel, useClips, useCreateClip, useDeleteChannel, useDeleteClip, useMarker, useNow, usePatchChannel, useProgrammes, type Channel, type Clip } from '~/app/live';

type Tab = 'monitor' | 'stats' | 'ingest' | 'output' | 'recording' | 'programmes' | 'catchup' | 'clips' | 'ads' | 'health' | 'availability' | 'settings';
const TABS: Tab[] = ['monitor', 'stats', 'ingest', 'output', 'recording', 'programmes', 'catchup', 'clips', 'ads', 'health', 'availability', 'settings'];
const INGEST_HOST = 'ingest-poc.vustream.net';

export const Route = createFileRoute('/_layout/t/$slug/live/$channelId')({
  component: Screen,
  // view / focus: the Catch-up entry of the main menu and the catch-up landing open the programme list (and a programme)
  validateSearch: (s: Record<string, unknown>): { tab?: Tab; view?: 'list' | 'timeline'; focus?: string } => ({
    ...(TABS.includes(s['tab'] as Tab) ? { tab: s['tab'] as Tab } : {}),
    ...(s['view'] === 'list' || s['view'] === 'timeline' ? { view: s['view'] as 'list' | 'timeline' } : {}),
    ...(typeof s['focus'] === 'string' && /^[\w-]{1,64}$/.test(s['focus']) ? { focus: s['focus'] as string } : {}),
  }),
});

function Screen() {
  const { t } = useLingui();
  const { slug, channelId } = Route.useParams();
  const { tab = 'monitor', view: cuView, focus: cuFocus } = Route.useSearch();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const q = useChannel(tenant.slug, channelId);
  React.useEffect(() => { document.title = appTitle(q.data?.title ?? t`Channel`); }, [t, q.data?.title]);
  const setTab = (v: string) => void navigate({ to: '/t/$slug/live/$channelId', params: { slug, channelId }, search: { tab: v as Tab }, replace: true } as never);

  if (q.isPending) return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.isNotFound) return <EmptyState title={t`Channel not found`} actions={<BackLink />} />;
    return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load the channel`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  }
  const ch = q.data;
  return (
    <div>
      <div className="mb-2"><BackLink /></div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{ch.title ?? ch.slug}</h1>
        <ChannelStateBadge state={ch.state} />
        <InternalBadge ch={ch} />
        <FeedBadges ch={ch} className="text-xs" />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="monitor">{t`Monitor`}</TabsTrigger>
          <TabsTrigger value="stats">{t`Statistics`}</TabsTrigger>
          <TabsTrigger value="catchup">{t`Catch-up`}</TabsTrigger>
          <TabsTrigger value="clips">{t`Clips`}</TabsTrigger>
          <TabsTrigger value="programmes">{t`Programmes`}</TabsTrigger>
          <TabsTrigger value="ingest">{t`Ingest`}</TabsTrigger>
          <TabsTrigger value="output">{t`Output`}</TabsTrigger>
          <TabsTrigger value="recording">{t`Recording`}</TabsTrigger>
          <TabsTrigger value="ads">{t`Ads`}</TabsTrigger>
          <TabsTrigger value="health">{t`Health`}</TabsTrigger>
          <TabsTrigger value="availability">{t`Availability & events`}</TabsTrigger>
          <TabsTrigger value="settings">{t`Settings`}</TabsTrigger>
        </TabsList>
        <TabsContent value="monitor" className="pt-4"><Monitor ch={ch} /></TabsContent>
        <TabsContent value="stats" className="pt-4">{tab === 'stats' ? <ChannelStats channelSlug={ch.slug!} /> : null}</TabsContent>
        <TabsContent value="catchup" className="pt-4">{tab === 'catchup' ? <CatchUp key={`${cuView ?? ''}|${cuFocus ?? ''}`} ch={ch} initialView={cuView} focus={cuFocus} /> : null}</TabsContent>
        <TabsContent value="clips" className="pt-4"><Clips ch={ch} /></TabsContent>
        <TabsContent value="programmes" className="pt-4"><Programmes ch={ch} /></TabsContent>
        <TabsContent value="ingest" className="pt-4"><Ingest ch={ch} /></TabsContent>
        <TabsContent value="output" className="pt-4"><Output ch={ch} /></TabsContent>
        <TabsContent value="recording" className="pt-4"><Recording ch={ch} /></TabsContent>
        <TabsContent value="ads" className="pt-4">{tab === 'ads' ? <ChannelAds channelId={ch.id!} /> : null}</TabsContent>
        <TabsContent value="health" className="pt-4">{tab === 'health' ? (
          <div className="space-y-6"><InputMonitorPanel channelId={ch.id!} /><LipSyncHealth channelId={ch.id!} /></div>
        ) : null}</TabsContent>
        <TabsContent value="availability" className="pt-4">{tab === 'availability' ? <ChannelAvailability channelId={ch.id!} /> : null}</TabsContent>
        <TabsContent value="settings" className="pt-4">{tab === 'settings' ? <SettingsTab ch={ch} /> : null}</TabsContent>
      </Tabs>
    </div>
  );
  function BackLink() {
    return <Link to={'/t/$slug/live' as never} params={{ slug } as never} className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="size-4 icon-dir" aria-hidden /> {t`Live`}</Link>;
  }
}

function Monitor({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const feeds = feedsOf(ch);
  const { tenant } = useTenant();
  const alarms: string[] = [];
  // A channel whose feed never arrived is waiting, not failing (e.g. a new channel before the customer's encoder
  // pushes): show a neutral note instead of red alarms.
  const waiting = waitingForFirstFeed(ch);
  if (!waiting) {
    if (ch.state === 'degraded') alarms.push(t`The channel is degraded: a configured encoder is not up.`);
    if (ch.state === 'stopped') alarms.push(t`The channel is stopped. The slate is shown to viewers until the feed returns.`);
    for (const f of feeds) if (f.state === 'down') alarms.push(t`Feed ${f.role.toUpperCase()} (${f.encoder}) is down — viewers see the slate.`);
  }
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)]">
      <PlayerLiveMonitor ch={ch} />
      <div className="space-y-4">
        <section aria-labelledby="feeds-h" className="rounded-lg border border-border bg-surface p-3">
          <h3 id="feeds-h" className="mb-2 font-semibold">{t`Feeds`}</h3>
          <ul className="space-y-2 text-sm">
            {feeds.map((f) => (
              <li key={f.role} className="flex items-center justify-between gap-2">
                <span>{t`Feed ${f.role.toUpperCase()}`} <span className="text-fg-muted ltr-isolate">({f.encoder})</span></span>
                <span className="flex items-center gap-2">
                  <Badge tone={f.state === 'up' ? 'ok' : f.state === 'down' ? 'danger' : 'neutral'}>{f.state}</Badge>
                  {f.at ? <span className="text-xs text-fg-muted num" title={t`Last report`}>{fmtDateTime(f.at, { locale, tz }, { dateStyle: undefined, timeStyle: 'medium' })}</span> : null}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-fg-muted">{t`SRT statistics (bitrate, loss, RTT) arrive with the feed-stats API.`}</p>
        </section>
        <section aria-labelledby="alarms-h" className="rounded-lg border border-border bg-surface p-3">
          <h3 id="alarms-h" className="mb-2 font-semibold">{t`Alarms`}</h3>
          {waiting ? <p className="text-sm text-fg-muted" data-testid="waiting-feed">{t`Waiting for the first feed. Viewers see the slate until the encoder starts pushing to the ingest endpoint.`}</p>
          : alarms.length === 0 ? <p className="text-sm text-fg-muted">{t`No alarms.`}</p> : (
            <ul className="space-y-1 text-sm">{alarms.map((a) => <li key={a} className="flex gap-2"><AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />{a}</li>)}</ul>
          )}
        </section>
        <section aria-labelledby="actions-h" className="rounded-lg border border-border bg-surface p-3">
          <h3 id="actions-h" className="mb-2 font-semibold">{t`Actions`}</h3>
          <IngestQuickControls channelId={ch.id!} />
          <p className="mt-2 text-xs text-fg-muted">{t`The slate switches in automatically within seconds when the feed is lost.`}</p>
        </section>
      </div>
      <div className="lg:col-span-2">
        {isInternal(ch) ? (
          <p className="rounded-lg border border-border p-3 text-sm text-fg-muted" data-testid="internal-no-embed">{t`An internal channel has no embed code or share link: viewers never see it. Its playback addresses in Output work for Studio and API keys only.`}</p>
        ) : <EmbedShare tenant={tenant.slug} kind="live" id={ch.slug!} src={ch.playback?.live} title={ch.title ?? ch.slug} idPrefix="live-es" />}
      </div>
    </div>
  );
}

function Copyable({ value, label }: { value: string; label: string }) {
  const { t } = useLingui();
  const toast = useToast();
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded bg-surface-2 px-2 py-1 text-xs ltr-isolate" title={value}>{value}</code>
      <Button size="icon" variant="ghost" aria-label={t`Copy ${label}`} onClick={() => { void navigator.clipboard?.writeText(value); toast.push({ title: t`Copied`, tone: 'ok' }); }}><Copy className="size-4" aria-hidden /></Button>
    </div>
  );
}

/** SRT passphrase: generated by the API (sealed at rest), shown only after the user re-enters their password, rotatable.
 *  Stored only for now: the encoder enforces it after a coordinated OME restart by Interhost. */
function SrtPassphrase({ ch, masked, legacy }: { ch: Channel; masked: boolean; legacy?: string }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const ing = (ch.ingest ?? {}) as Record<string, unknown>;
  const isSet = ing['srt_passphrase_set'] === true;
  const setAt = typeof ing['srt_passphrase_set_at'] === 'string' ? (ing['srt_passphrase_set_at'] as string) : undefined;
  const [revealed, setRevealed] = React.useState<string | null>(null);
  const [ask, setAsk] = React.useState(false);
  const [confirmRotate, setConfirmRotate] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => { if (!revealed) return; const i = window.setTimeout(() => setRevealed(null), 60_000); return () => window.clearTimeout(i); }, [revealed]);
  const rotate = async () => {
    setBusy(true);
    try {
      const r = await api.POST('/v1/channels/{id}/ingest/srt-passphrase', { params: { path: { id: ch.id! } } });
      if (r.error) throw new Error(r.error.detail ?? r.error.title);
      setRevealed(null); setConfirmRotate(false);
      toast.push({ title: isSet ? t`New SRT passphrase generated` : t`SRT passphrase generated`, tone: 'ok' });
      await qc.invalidateQueries({ queryKey: ['t', tenant.slug] });
    } catch (e) { toast.push({ title: t`Could not generate the passphrase`, description: String((e as Error).message ?? e), tone: 'danger' }); }
    finally { setBusy(false); }
  };
  const reveal = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const r = await api.POST('/v1/channels/{id}/ingest/srt-passphrase/reveal', { params: { path: { id: ch.id! } }, body: { password } });
      if (r.error || !r.data) { setErr(r.response.status === 403 ? t`Wrong password` : (r.error?.detail ?? t`Could not reveal the passphrase`)); return; }
      setRevealed(r.data.srt_passphrase); setAsk(false); setPassword('');
    } finally { setBusy(false); }
  };
  if (masked) {
    return <Field id="secret" label={t`SRT passphrase`} hint={t`Only Engineers and above can see ingest secrets.`}><code className="rounded bg-surface-2 px-2 py-1 text-xs ltr-isolate">••••••••</code></Field>;
  }
  return (
    <>
    <Field id="secret" label={t`SRT passphrase`} hint={t`Stored now; the encoder starts requiring it after Interhost applies it (a coordinated encoder restart). Share it with the encoder operator only.`}>
      <div className="flex flex-wrap items-center gap-2" data-testid="srt-passphrase">
        {revealed ? <Copyable value={revealed} label={t`SRT passphrase`} />
          : <code className="rounded bg-surface-2 px-2 py-1 text-xs ltr-isolate">{isSet ? '••••••••••••••••' : legacy ? legacy : t`not set`}</code>}
        {isSet && !revealed ? <Button size="sm" variant="secondary" onClick={() => setAsk(true)}>{t`Show`}</Button> : null}
        {isSet ? <Button size="sm" variant="ghost" onClick={() => setConfirmRotate(true)}>{t`Rotate`}</Button>
          : <Button size="sm" onClick={() => void rotate()} loading={busy}>{t`Generate`}</Button>}
        {setAt ? <span className="text-xs text-fg-muted">{t`Set`} <span className="num">{fmtDateTime(setAt, { locale, tz })}</span></span> : null}
      </div>
    </Field>
      <Dialog open={ask} onOpenChange={(o) => { setAsk(o); if (!o) { setPassword(''); setErr(null); } }}>
        <DialogContent title={t`Confirm it's you`} description={t`Enter your password to show the SRT passphrase. It hides again after a minute.`} closeLabel={t`Close`}>
          <form onSubmit={(e) => void reveal(e)} className="mt-4 space-y-3">
            <Input type="password" required autoComplete="current-password" aria-label={t`Password`} value={password} onChange={(e) => setPassword(e.target.value)} />
            {err ? <p role="alert" className="text-sm text-danger">{err}</p> : null}
            <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={() => setAsk(false)}>{t`Cancel`}</Button><Button type="submit" loading={busy}>{t`Show`}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <DialogContent title={t`Rotate the SRT passphrase?`} description={t`The old passphrase stops being valid once the new one is applied on the encoder; the contribution encoder must be updated at the same time.`} closeLabel={t`Close`}>
          <div className="mt-4 flex justify-end gap-2"><Button variant="ghost" onClick={() => setConfirmRotate(false)}>{t`Cancel`}</Button><Button variant="danger" loading={busy} onClick={() => void rotate()}>{t`Rotate`}</Button></div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Ingest({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const path = `${tenant.slug}/${ch.slug}`;
  const srt = `srt://${INGEST_HOST}:9999?streamid=${encodeURIComponent(`srt://${INGEST_HOST}:9999/${path}`)}`;
  const rtmp = `rtmp://${INGEST_HOST}:1935/${tenant.slug}/${ch.slug}`;
  const ingest = (ch.ingest ?? {}) as Record<string, string>;
  const masked = !can(scopes, 'channels:operate');
  return (
    <div className="space-y-6">
      <IngestSettings channelId={ch.id!} />
      <details className="max-w-3xl rounded-lg border border-border bg-surface p-3" data-testid="ingest-legacy">
        <summary className="cursor-pointer font-medium">{t`Shared endpoint (before per-channel settings)`}</summary>
        <div className="mt-3 space-y-4">
          <div className="rounded-lg border border-border bg-surface-2 p-3 text-sm">
            {t`The shared endpoint stays closed to the internet. Use a push feed above: it gets its own port, and you manage the encoder addresses allowed to send there.`}
          </div>
          <Field id="srt" label={t`SRT (caller → listener)`}><Copyable value={srt} label="SRT" /></Field>
          <Field id="rtmp" label={t`RTMP`}><Copyable value={rtmp} label="RTMP" /></Field>
          <SrtPassphrase ch={ch} masked={masked} legacy={ingest['srt_passphrase']} />
        </div>
      </details>
      <ContributionHint rec={(ch as Channel & { contribution?: ContributionRec }).contribution} />
      {can(scopes, 'channels:write') ? <Link to="/t/$slug/contribution" params={{ slug: tenant.slug }} className="text-sm text-primary underline" data-testid="contribution-sheet-link">{t`Contribution sheet for the broadcast engineer (all channels)`}</Link> : null}
    </div>
  );
}

function Output({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  return (
    <div className="max-w-3xl space-y-4">
      <section className="space-y-2 rounded-lg border border-border bg-surface p-3" aria-labelledby="out-preset-h">
        <h2 id="out-preset-h" className="text-sm font-semibold">{t`Player`}</h2>
        <PresetPicker tenant={tenant.slug} scope="channel" targetId={ch.id!} kind="live" contentId={ch.slug!} canWrite={can(scopes, 'delivery:write')} idPrefix="channel-preset" />
      </section>
      <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
        <dt className="text-fg-muted">{t`Ladder`}</dt><dd className="ltr-isolate">{ch.ladder}</dd>
        <dt className="text-fg-muted">{t`Latency mode`}</dt><dd>{t`Low-latency HLS (standard HLS for older players)`}</dd>
        <dt className="text-fg-muted">{t`Deinterlace`}</dt><dd className="ltr-isolate">{ch.deinterlace ?? 'auto'}</dd>
      </dl>
      {ch.playback?.live ? <Field id="live-url" label={t`Live HLS`}><Copyable value={ch.playback.live} label={t`Live HLS`} /></Field> : null}
      {ch.playback?.start_over ? <Field id="so-url" label={t`Start-over template`}><Copyable value={ch.playback.start_over} label={t`Start-over template`} /></Field> : null}
      {ch.playback?.catch_up ? <Field id="cu-url" label={t`Catch-up template`}><Copyable value={ch.playback.catch_up} label={t`Catch-up template`} /></Field> : null}
      {!isInternal(ch) ? <BackupPathway channelId={ch.id!} /> : null}
      <EncodersCard channelId={ch.id!} />
    </div>
  );
}

function Recording({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  return (
    <div className="space-y-6">
      <RecordingMode channelId={ch.id!} />
      <dl className="grid max-w-xl grid-cols-[12rem_1fr] gap-y-2 text-sm">
        <dt className="text-fg-muted">{t`Recording`}</dt><dd>{t`On (continuous)`}</dd>
        <dt className="text-fg-muted">{t`Retention`}</dt><dd>{t`${ch.retention_days ?? 0} days`}</dd>
        <dt className="text-fg-muted">{t`DVR window for viewers`}</dt><dd className="num">{hms((ch.dvr_window_s ?? 0) * 1000)}</dd>
      </dl>
      <LiveToVODRuleEditor ch={ch} />
    </div>
  );
}

/** Live-to-VOD rule (change catchup-by-programme): programmes matching the filters become Library items after they end. */
function LiveToVODRuleEditor({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const q = useLiveToVODRule(tenant.slug, ch.id!);
  const put = usePutLiveToVODRule(tenant.slug, ch.id!);
  const [r, setR] = React.useState<LiveToVODRule | null>(null);
  const [cats, setCats] = React.useState('');
  React.useEffect(() => { if (q.data) { setR(q.data); setCats(q.data.categories.join(', ')); } }, [q.data]);
  const canWrite = can(scopes, 'channels:write') && can(scopes, 'assets:write');
  if (q.isPending || !r) return <Skeleton className="h-40 w-full max-w-xl" />;
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load the live-to-VOD rule`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  const set = (p: Partial<LiveToVODRule>) => setR((x) => (x ? { ...x, ...p } : x));
  const save = () => put.mutate({ ...r, categories: cats.split(',').map((c) => c.trim()).filter(Boolean) }, {
    onSuccess: () => toast.push({ title: t`Live-to-VOD rule saved`, tone: 'ok' }),
    onError: (e) => toast.push({ title: t`Could not save the rule`, description: e instanceof ApiError ? (e.problem?.detail ?? e.message) : String(e), tone: 'danger' }),
  });
  return (
    <form className="max-w-xl space-y-3 rounded-lg border border-border bg-surface p-4" onSubmit={(e) => { e.preventDefault(); save(); }} aria-labelledby="l2v-h" data-testid="l2v-rule">
      <h3 id="l2v-h" className="font-semibold">{t`Live-to-VOD`}</h3>
      <p className="text-sm text-fg-muted">{t`Programmes that match are cut from the recording and added to the Library automatically after they end (only fully recorded programmes inside the retention window).`}</p>
      <div className="flex items-center gap-2">
        <Switch id="l2v-enabled" checked={r.enabled} onCheckedChange={(v) => set({ enabled: v })} disabled={!canWrite} />
        <label htmlFor="l2v-enabled" className="text-sm">{r.enabled ? t`On` : t`Off`}</label>
      </div>
      <Field id="l2v-min" label={t`Minimum duration (minutes)`}><Input type="number" min={0} max={720} value={String(Math.round(r.min_duration_s / 60))} onChange={(e) => set({ min_duration_s: Number(e.target.value) * 60 })} disabled={!canWrite} /></Field>
      <Field id="l2v-cats" label={t`Categories`} hint={t`Comma separated; empty = any category.`}><Input value={cats} onChange={(e) => setCats(e.target.value)} disabled={!canWrite} /></Field>
      {(ch as Channel & { clip_source_id?: string | null }).clip_source_id ? (
        <Field id="l2v-source" label={t`Picture`} hint={t`Clean feed: the programme is published without logo and tickers (c14-clean-feed).`}>
          <Select ariaLabel={t`Picture`} value={(r as LiveToVODRule & { source?: string }).source || 'program'} onValueChange={(v) => { if (canWrite) set({ source: v } as Partial<LiveToVODRule>); }}
            options={[{ value: 'program', label: t`Program feed` }, { value: 'clean', label: t`Clean feed` }]} />
        </Field>
      ) : null}
      <Field id="l2v-title" label={t`Title pattern`} hint={t`Regular expression, case-insensitive; empty = any title.`}><Input value={r.title_pattern} onChange={(e) => set({ title_pattern: e.target.value })} className="ltr-isolate" dir="ltr" disabled={!canWrite} /></Field>
      <PublishRuleSection value={r.collection} onChange={(v) => set({ collection: v })} disabled={!canWrite} />
      <Field id="l2v-publish" label={t`Publishing`}>
        <Select ariaLabel={t`Publishing`} value={r.publish} onValueChange={(v) => set({ publish: v as 'auto' | 'manual' })} options={[{ value: 'manual', label: t`Keep as draft` }, { value: 'auto', label: t`Publish automatically` }]} />
      </Field>
      <Field id="l2v-delay" label={t`Delay after the programme ends (seconds)`}><Input type="number" min={0} max={86400} value={String(r.delay_s)} onChange={(e) => set({ delay_s: Number(e.target.value) })} disabled={!canWrite} /></Field>
      {canWrite ? <Button type="submit" loading={put.isPending} data-testid="l2v-save">{t`Save`}</Button> : <p className="text-xs text-fg-muted">{t`Only Engineers and above can change the live-to-VOD rule.`}</p>}
    </form>
  );
}

function PublishRuleSection({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const sections = useSections(tenant.slug);
  const options = [{ value: 'none', label: t`No section` }, { value: AUTO_SECTION, label: t`Section named after the programme` },
    ...(sections.data ?? []).map((s) => ({ value: s.id, label: s.parent_id ? `— ${s.name}` : s.name }))];
  return (
    <Field id="l2v-section" label={t`Library section`}>
      {disabled ? <Input value={options.find((o) => o.value === (value || 'none'))?.label ?? ''} disabled /> : <Select ariaLabel={t`Library section`} value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)} options={options} />}
    </Field>
  );
}

function Programmes({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const now = useNow(30_000);
  const q = useProgrammes(tenant.slug, ch.id!, now - 24 * 3600_000, now + 24 * 3600_000);
  const marker = useMarker(tenant.slug, ch.id!);
  const [title, setTitle] = React.useState('');
  const canWrite = can(scopes, 'channels:write');
  const fmt = (s: string) => fmtDateTime(s, { locale, tz });
  return (
    <div className="space-y-4">
      <p className="text-sm">
        <Link to={'/t/$slug/epg' as never} params={{ slug: tenant.slug } as never} search={{ channel: ch.id } as never} className="text-primary underline-offset-4 hover:underline" data-testid="open-epg">{t`Open the EPG — week guide, XMLTV import and export links`}</Link>
      </p>
      {canWrite ? (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => {
          e.preventDefault();
          marker.mutate(title || t`Programme`, { onSuccess: () => { setTitle(''); toast.push({ title: t`New programme started`, tone: 'ok' }); }, onError: (err) => toast.push({ title: t`Could not start a programme`, description: err instanceof ApiError ? (err.problem?.detail ?? err.message) : String(err), tone: 'danger' }) });
        }}>
          <Field id="prog-title" label={t`Title of the next programme`}><Input value={title} onChange={(e) => setTitle(e.target.value)} className="w-72" /></Field>
          <Button type="submit" loading={marker.isPending}>{t`Start next programme now`}</Button>
        </form>
      ) : null}
      {q.isPending ? <Skeleton className="h-32 w-full" /> : q.isError ? <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load programmes`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} /> : q.data.length === 0 ? (
        <EmptyState title={t`No programmes in the last and next 24 hours`} description={t`Start one with a marker, or import a guide in the EPG section.`} />
      ) : (
        <table className="w-full text-sm">
          <thead className="text-start text-fg-muted"><tr><th className="py-1 text-start font-medium">{t`Start`}</th><th className="text-start font-medium">{t`End`}</th><th className="text-start font-medium">{t`Title`}</th><th className="text-start font-medium">{t`Source`}</th><th /></tr></thead>
          <tbody>
            {[...q.data].sort((a, b) => Date.parse(b.start_at!) - Date.parse(a.start_at!)).map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="py-1.5 num">{fmt(p.start_at!)}</td>
                <td className="num">{p.end_at ? fmt(p.end_at) : <Badge tone="live">{t`on air`}</Badge>}</td>
                <td><span className="flex flex-wrap items-center gap-2"><bdi dir="auto">{p.title ?? '—'}</bdi><ExclusionBadge s={exclusionOf(p)} /></span></td>
                <td>{p.source}</td>
                <td className="text-end">
                  {ch.playback?.start_over && !p.end_at && !isExcluded(p) ? <Copyable value={ch.playback.start_over.replace('{programme_id}', p.id!)} label={t`Start-over URL`} /> : null}
                  <ProgrammeExclusionAction programmeId={p.id!} s={exclusionOf(p)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Clips({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const q = useClips(tenant.slug, ch.id!);
  const del = useDeleteClip(tenant.slug);
  const create = useCreateClip(tenant.slug);
  const [playing, setPlaying] = React.useState<Clip | null>(null);
  const [deleting, setDeleting] = React.useState<Clip | null>(null);
  const canWrite = can(scopes, 'clips:write');
  // clip ranges to the second, 24 h Israel (tenant) time in every locale (QA 2026-09-28 #8, #10)
  const fmt = (s: string) => fmtDateTime(s, { locale, tz }, { timeStyle: 'medium', hourCycle: 'h23' });
  const fmtT = (s: string) => fmtDateTime(s, { locale, tz }, { dateStyle: undefined, timeStyle: 'medium', hourCycle: 'h23' });
  const clips = (q.data ?? []).filter((c) => c.status !== 'deleted');
  const copyOf = (c: Clip) => clips.find((x) => x.id !== c.id && x.precision === 'frame' && x.start_at === c.start_at && x.end_at === c.end_at && (x.title ?? '') === (c.title ?? ''));

  const finalize = async (c: Clip) => {
    try {
      await create.mutateAsync({ channel_id: ch.id!, start_at: c.start_at!, end_at: c.end_at!, title: c.title ? `${c.title}` : undefined, precision: 'frame', renditions: c.renditions });
      toast.push({ title: t`Frame-accurate copy started`, description: t`It is kept after the recording expires.`, tone: 'ok' });
    } catch (e) {
      toast.push({ title: t`Could not finalize`, description: e instanceof ApiError ? (e.problem?.detail ?? e.message) : String(e), tone: 'danger' });
    }
  };

  if (q.isPending) return <Skeleton className="h-40 w-full" />;
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load clips`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  if (clips.length === 0) return <EmptyState title={t`No clips yet`} description={t`Open Catch-up, mark in and out, and publish.`} />;
  return (
    <>
      <table className="w-full text-sm" data-testid="clips-table">
        <thead className="text-fg-muted"><tr>
          <th className="py-1 text-start font-medium">{t`Title`}</th><th className="text-start font-medium">{t`Range`}</th><th className="text-start font-medium">{t`Duration`}</th>
          <th className="text-start font-medium">{t`Precision`}</th><th className="text-start font-medium">{t`Status`}</th><th className="text-end font-medium">{t`Actions`}</th>
        </tr></thead>
        <tbody>
          {clips.map((c) => {
            const dur = c.start_at && c.end_at ? Date.parse(c.end_at) - Date.parse(c.start_at) : 0;
            const copy = c.precision === 'segment' ? copyOf(c) : undefined;
            // the rendered MP4s of an AI clip (clips.variants: vertical-9x16, landscape-16x9, square-1x1 — change
            // add-viewstream-v3 ai-clips; 16:9 and 1:1 since 2026-10-13)
            const variants = Object.entries((c as Clip & { variants?: Record<string, { status?: string; url?: string }> }).variants ?? {})
              .map(([name, v]) => ({ name, v, label: name === 'landscape-16x9' ? '16:9' : name === 'square-1x1' ? '1:1' : '9:16' }));
            return (
              <tr key={c.id} className="border-t border-border align-top" data-clip={c.id} data-status={c.status}>
                <td className="py-1.5">
                  <div>{c.title ?? <span className="text-fg-muted">{t`(untitled)`}</span>}</div>
                  <ClipFallbackBadge clip={c as Clip & { source_fallback?: boolean }} />
                  <VideoId id={c.id} className="max-w-full" />
                </td>
                <td className="num">{c.start_at ? `${fmt(c.start_at)} – ${fmtT(c.end_at!)}` : '—'}</td>
                <td className="num">{hms(dur)}</td>
                <td>{c.precision === 'frame' ? t`Frame` : t`Segment`}</td>
                <td>
                  <div className="flex flex-col gap-1">
                    <ClipStatusBadge status={c.status} />
                    {c.precision === 'segment' && c.expires_at ? <span className="text-xs text-fg-muted" data-testid="expiry">{t`Expires with the recording on ${fmt(c.expires_at)}`}</span> : null}
                    {copy ? <span className="text-xs text-fg-muted">{t`Frame-accurate copy:`} <ClipStatusBadge status={copy.status} /></span> : null}
                    {variants.map(({ name, v, label }) => (
                      <span key={name} className="text-xs text-fg-muted" data-testid="clip-vertical">
                        <bdi dir="ltr">{label}</bdi>: {v.status === 'ready' ? t`Ready` : v.status === 'failed' ? t`Failed` : t`Rendering`}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="text-end">
                  <div className="flex justify-end gap-1">
                    {c.playback?.hls ? <Button size="icon" variant="ghost" aria-label={t`Play ${c.title ?? ''}`} onClick={() => setPlaying(c)}><Play className="size-4" aria-hidden /></Button> : null}
                    {variants.filter(({ v }) => v.status === 'ready' && v.url).map(({ name, v, label }) => (
                      <a key={name} href={v.url} download className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs hover:bg-surface-2"
                        aria-label={t`Download the ${label} MP4`} title={t`Download the ${label} MP4`} data-testid="clip-vertical-download">
                        <Download className="size-4" aria-hidden /> <bdi dir="ltr">{label}</bdi>
                      </a>
                    ))}
                    {c.playback?.hls ? <Button size="icon" variant="ghost" aria-label={t`Copy share link`} onClick={() => { void navigator.clipboard?.writeText(watchUrl(tenant.slug, 'clip', c.id!)); toast.push({ title: t`Share link copied`, tone: 'ok' }); }}><Copy className="size-4" aria-hidden /></Button> : null}
                    {canWrite && c.precision === 'segment' && c.status === 'ready' && !copy ? (
                      <Button size="sm" variant="secondary" onClick={() => void finalize(c)} loading={create.isPending} title={t`Creates a frame-accurate copy that is kept after the recording expires`}><Wand2 className="size-4" aria-hidden /> {t`Finalize`}</Button>
                    ) : null}
                    {canWrite ? <Button size="icon" variant="ghost" aria-label={t`Delete ${c.title ?? ''}`} onClick={() => setDeleting(c)}><Trash2 className="size-4" aria-hidden /></Button> : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Dialog open={!!playing} onOpenChange={(o) => { if (!o) setPlaying(null); }}>
        <DialogContent title={playing?.title ?? t`Clip`} closeLabel={t`Close`} className="max-w-3xl">
          {playing?.playback?.hls ? <Player mode="main" autoplay src={playing.playback.hls} label={playing.title ?? t`Clip`} /> : null}
          {playing?.playback?.hls ? <div className="mt-3 space-y-3"><Copyable value={playing.playback.hls} label="HLS" /><EmbedShare tenant={tenant.slug} kind="clip" id={playing.id!} src={playing.playback.hls} title={playing.title} idPrefix="clip-es" compact /></div> : null}
        </DialogContent>
      </Dialog>
      <ConfirmByNameDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }} name={deleting?.title || deleting?.id || ''}
        title={t`Delete clip`} description={t`Its URLs stop working immediately (410). This cannot be undone.`}
        labels={{ field: t`Type the clip's name to confirm`, confirm: t`Delete`, cancel: t`Cancel`, close: t`Close` }}
        busy={del.isPending}
        onConfirm={async () => { if (!deleting) return; await del.mutateAsync(deleting.id!); setDeleting(null); toast.push({ title: t`Clip deleted`, tone: 'ok' }); }} />
    </>
  );
}

/** Channel → Settings: Simple = name, rewind window and packaging; Advanced adds the technical settings, the lip-sync
 *  monitor and blackouts (studio change settings-ux). */
function SettingsTab({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const [mode] = useUiMode();
  return (
    <div className="space-y-6" data-testid="channel-settings">
      <ModeToggle />
      <Settings ch={ch} advanced={mode === 'advanced'} />
      <div className="max-w-2xl rounded-lg border border-border p-4"><ChannelPackaging channelId={ch.id!} /></div>
      {mode === 'advanced' ? (
        <>
          <ChannelPlacementSettings key={`${ch.id}|${ch.updated_at ?? ''}`} ch={ch} />
          <CleanLinkCard channelId={ch.id!} />
          <LipSyncSwitches channelId={ch.id!} />
          <ChannelBlackouts channelId={ch.id!} />
        </>
      ) : <p className="text-sm text-fg-muted">{t`Who sees the channel, its AI switches and clip source, the encoder, the lip-sync monitor, blackouts, recording retention and deinterlacing are under Advanced.`}</p>}
      <DeleteChannel ch={ch} />
    </div>
  );
}

function Settings({ ch, advanced }: { ch: Channel; advanced: boolean }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const patch = usePatchChannel(tenant.slug, ch.id!);
  const [title, setTitle] = React.useState(ch.title ?? '');
  const [dvr, setDvr] = React.useState(String(Math.round((ch.dvr_window_s ?? 7200) / 60)));
  const [ret, setRet] = React.useState(String(ch.retention_days ?? 7));
  const [dei, setDei] = React.useState<string>(ch.deinterlace ?? 'auto');
  const canWrite = can(scopes, 'channels:write');
  const dvrN = Number(dvr), retN = Number(ret);
  const dvrErr = Number.isInteger(dvrN) && dvrN >= 1 && dvrN <= 1440 ? undefined : t`Enter whole minutes from 1 to 1440 (24 hours).`;
  const retErr = Number.isInteger(retN) && retN >= 1 && retN <= 366 ? undefined : t`Enter whole days from 1 to 366.`;
  const titleErr = title.trim() ? undefined : t`Enter a name for the channel.`;
  const save = () => {
    if (dvrErr || retErr || titleErr) return;
    patch.mutate({ title: title.trim(), dvr_window_s: dvrN * 60, retention_days: retN, deinterlace: dei }, {
      onSuccess: () => toast.push({ title: t`Channel saved`, tone: 'ok' }),
      onError: (e) => toast.push({ title: t`Could not save the channel. Try again.`, description: e instanceof ApiError ? (e.problem?.detail ?? e.message) : String(e), tone: 'danger' }),
    });
  };
  return (
    <form className="max-w-2xl rounded-lg border border-border p-4" onSubmit={(e) => { e.preventDefault(); save(); }} noValidate>
      <h2 className="mb-3 text-lg font-semibold">{t`Channel`}</h2>
      <div className="max-w-md">
        <Field id="ch-title" label={t`Name`} hint={t`Shown to viewers in the player and on your site.`} error={titleErr}><Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canWrite} /></Field>
        <Field id="ch-dvr" label={t`Rewind window (minutes)`} hint={t`How far back viewers can rewind the live stream.`} error={dvrErr}><Input type="number" inputMode="numeric" min={1} max={1440} value={dvr} onChange={(e) => setDvr(e.target.value)} disabled={!canWrite} className="num w-32" /></Field>
        {advanced ? (
          <>
            <Field id="ch-slug" label={t`Slug`} hint={t`Part of the stream addresses; cannot be changed.`}><Input value={ch.slug} disabled className="ltr-isolate" /></Field>
            <Field id="ch-ret" label={t`Keep recordings for (days)`} hint={t`Catch-up and clips older than this are deleted.`} error={retErr}><Input type="number" inputMode="numeric" min={1} max={366} value={ret} onChange={(e) => setRet(e.target.value)} disabled={!canWrite} className="num w-32" /></Field>
            <p className="mb-4 -mt-2 text-xs text-fg-muted" data-testid="exclusions-pointer">
              {t`Placeholders that should not be kept (e.g. the Shabbat still) are excluded by title in`}{' '}
              <Link to={'/t/$slug/settings' as never} params={{ slug: tenant.slug } as never} search={{ tab: 'catchup' } as never} className="text-primary underline-offset-4 hover:underline">{t`Settings → Recording / Catch-up`}</Link>
            </p>
            <Field id="ch-dei" label={t`Deinterlace`} hint={t`Smooths interlaced (1080i) sources. Automatic detects it.`}>
              <Select ariaLabel={t`Deinterlace`} value={dei} onValueChange={setDei} options={[{ value: 'auto', label: t`Automatic` }, { value: 'on', label: t`Always` }, { value: 'off', label: t`Never` }]} />
            </Field>
          </>
        ) : null}
      </div>
      {canWrite ? <Button type="submit" loading={patch.isPending} disabled={!!(dvrErr || retErr || titleErr)}>{t`Save`}</Button> : <p className="text-xs text-fg-muted">{t`Only Engineers and above can change channel settings.`}</p>}
    </form>
  );
}

/** Channel → Settings → danger zone: DELETE /v1/channels/{id} (channels:write, only a stopped channel). The channel goes
 *  to the trash: policies detached, encoder config removed, recordings kept until retention, then purged for good. */
function DeleteChannel({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const navigate = useNavigate();
  const del = useDeleteChannel(tenant.slug, ch.id!);
  const [open, setOpen] = React.useState(false);
  if (!can(scopes, 'channels:write')) return null;
  const stopped = channelDeletable(ch);
  const name = ch.slug ?? '';
  return (
    <section aria-labelledby="ch-del-h" className="max-w-2xl rounded-lg border border-danger/40 p-4" data-testid="channel-delete">
      <h2 id="ch-del-h" className="mb-2 text-lg font-semibold text-danger">{t`Delete channel`}</h2>
      <p className="mb-3 text-sm text-fg-muted">{t`The channel moves to the trash: its playback policies are detached and its encoder configuration is removed. Recordings, catch-up and clips stay until the recording retention ends; then they are deleted permanently together with the channel.`}</p>
      {stopped ? null : <p className="mb-3 flex items-center gap-1 text-sm" role="note"><AlertTriangle className="size-4 text-warn" aria-hidden />{t`Stop the channel before deleting it.`}</p>}
      <Button type="button" variant="danger" disabled={!stopped} onClick={() => setOpen(true)}><Trash2 className="me-1 size-4" aria-hidden />{t`Delete channel`}</Button>
      <ConfirmByNameDialog open={open} onOpenChange={setOpen} name={name}
        title={t`Delete channel ${ch.title ?? name}?`}
        description={t`Playback stops for good, the channel's playback policies are detached and its encoder configuration is removed. Recordings are kept until their retention ends and are then deleted permanently with the channel.`}
        labels={{ field: t`Type the channel's slug (${name}) to confirm`, confirm: t`Delete channel`, cancel: t`Cancel`, close: t`Close` }}
        busy={del.isPending}
        onConfirm={async () => {
          try {
            const r = await del.mutateAsync();
            setOpen(false);
            const until = r?.recordings_kept_until ?? r?.purge_at;
            toast.push({ title: t`Channel deleted`, description: until ? t`Recordings are kept until ${fmtDateTime(until, { locale, tz })}.` : undefined, tone: 'ok' });
            void navigate({ to: '/t/$slug/live', params: { slug: tenant.slug } } as never);
          } catch (e) {
            toast.push({ title: t`Could not delete the channel`, description: e instanceof ApiError ? (e.problem?.detail ?? e.message) : String(e), tone: 'danger' });
          }
        }} />
    </section>
  );
}
